
import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Recipe } from '@/types/recipe';
import RecipeCard from '@/components/RecipeCard';

interface RecentRecipesProps {
  recipes: Recipe[];
  isLoading: boolean;
}

const RecentRecipes: React.FC<RecentRecipesProps> = ({ recipes, isLoading }) => {
  // Get most recently added recipes (limit to 2)
  const recentRecipes = isLoading ? [] : [...recipes]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 2);

  // Mock delete handler since we don't need delete functionality on homepage
  const handleDelete = () => {
    // No-op for homepage display
  };

  return (
    <div>
      <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-4 mt-2">Recent Recipes</h2>
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-full flex flex-col border-golden-light bg-card shadow-sm rounded-lg">
              <div className="p-6 pb-2">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="flex-1 px-6">
                <Skeleton className="h-16 w-full mb-2" />
                <div className="flex gap-1">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-12" />
                </div>
              </div>
              <div className="p-6 pt-2">
                <Skeleton className="h-9 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : recentRecipes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {recentRecipes.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={{
                ...recipe,
                usageCount: undefined,
                lastUsedDate: undefined,
                daysSinceLastUse: undefined,
              }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-muted/30">
          <CardContent className="py-6">
            <div className="text-center">
              <p>You haven't added any recipes yet.</p>
              <Link to="/import-recipes" className="mt-4 inline-block">
                <Button>
                  Import Your First Recipe
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="mt-4 flex justify-end">
        <Link to="/recipe-collection">
          <Button variant="ghost" size="sm" className="flex items-center">
            Browse All Recipes
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default RecentRecipes;
